import { bakeColorGradeForEngine, evaluateKeyframes, type Project } from '@cinegrid/core';
import { resolveAssetUrl } from '../media/mediaStore';
import { Compositor, effectAmounts } from './compositor';

function seekVideo(video: HTMLVideoElement, time: number): Promise<void> {
  const t = Math.max(0, Math.min(time, Number.isFinite(video.duration) ? video.duration : time));
  if (Math.abs(video.currentTime - t) < 0.001 && video.readyState >= 2) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      video.removeEventListener('seeked', done);
      video.removeEventListener('loadeddata', done);
      resolve();
    };
    video.addEventListener('seeked', done);
    video.addEventListener('loadeddata', done);
    try {
      video.currentTime = t;
    } catch {
      resolve();
    }
    // Safety timeout if seek never fires
    setTimeout(done, 400);
  });
}

function ensureVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      video.removeEventListener('loadeddata', done);
      video.removeEventListener('canplay', done);
      resolve();
    };
    video.addEventListener('loadeddata', done);
    video.addEventListener('canplay', done);
    void video.play().then(() => video.pause()).catch(() => undefined);
    setTimeout(done, 2000);
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

/**
 * Offline / export frame renderer — same WebGL grade + composite path as the viewer.
 */
export class OfflineRenderer {
  readonly canvas: HTMLCanvasElement;
  private compositor: Compositor;
  private videos = new Map<string, HTMLVideoElement>();
  private images = new Map<string, HTMLImageElement>();
  private textures = new Map<string, WebGLTexture>();
  private textCanvas = document.createElement('canvas');

  constructor(canvas?: HTMLCanvasElement) {
    this.canvas = canvas ?? document.createElement('canvas');
    this.compositor = new Compositor(this.canvas);
  }

  async render(project: Project, time: number, width: number, height: number): Promise<void> {
    const comp = this.compositor;
    comp.beginFrame(width, height);

    const frameSize = { width, height };
    const videoTracks = [...project.tracks]
      .filter((t) => t.kind === 'video' && t.visible)
      .reverse();

    for (const track of videoTracks) {
      const clip = project.clips.find(
        (c) =>
          c.trackId === track.id && time >= c.start && time < c.start + c.duration
      );
      if (!clip) continue;

      let transform = { ...clip.properties.transform };
      let color = bakeColorGradeForEngine(clip.properties.color);
      const local = time - clip.start;
      const kfs = clip.properties.keyframes;

      for (const prop of [
        'opacity',
        'positionX',
        'positionY',
        'scaleX',
        'scaleY',
        'rotation',
      ] as const) {
        const v = evaluateKeyframes(kfs, prop, local);
        if (v != null) (transform as Record<string, number>)[prop] = v;
      }
      const exposure = evaluateKeyframes(kfs, 'exposure', local);
      const saturation = evaluateKeyframes(kfs, 'saturation', local);
      const hue = evaluateKeyframes(kfs, 'hue', local);
      if (exposure != null) color.exposure = exposure;
      if (saturation != null) color.saturation = saturation;
      if (hue != null) color.hue = hue;

      let dissolve = 0;
      if (clip.transitionOut && local > clip.duration - clip.transitionOut.duration) {
        dissolve =
          (local - (clip.duration - clip.transitionOut.duration)) /
          clip.transitionOut.duration;
      }

      if (clip.kind === 'adjustment') {
        comp.applyAdjustment({
          color,
          effects: effectAmounts(clip.properties.effects),
          transform,
        });
        continue;
      }

      if (clip.kind === 'solid') {
        const assetSolid = clip.assetId
          ? project.assets.find((a) => a.id === clip.assetId)
          : undefined;
        const hex =
          clip.properties.solidColor || assetSolid?.solidColor || '#808080';
        const texKey = `solid-${hex}`;
        let tex = this.textures.get(texKey);
        if (!tex) {
          tex = comp.createSolidTexture(hex);
          this.textures.set(texKey, tex);
        }
        comp.drawLayer({
          texture: tex,
          transform,
          color,
          crossDissolve: dissolve,
          frameSize,
          effects: effectAmounts(clip.properties.effects),
        });
        continue;
      }

      if (clip.kind === 'text' && clip.properties.text) {
        const tc = this.textCanvas;
        tc.width = width;
        tc.height = height;
        const ctx = tc.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, width, height);
          const t = clip.properties.text;
          const fontSize = Math.max(8, t.fontSize * (height / project.settings.height));
          ctx.font = `${t.fontWeight} ${fontSize}px ${t.fontFamily}`;
          ctx.fillStyle = t.color;
          ctx.textAlign = t.align;
          ctx.textBaseline = 'middle';
          const x =
            t.align === 'left' ? width * 0.05 : t.align === 'right' ? width * 0.95 : width / 2;
          if (t.strokeWidth > 0) {
            ctx.strokeStyle = t.strokeColor;
            ctx.lineWidth = t.strokeWidth;
            ctx.strokeText(t.content, x, height / 2);
          }
          ctx.fillText(t.content, x, height / 2);
        }
        const texKey = `text-${clip.id}`;
        let tex = this.textures.get(texKey);
        if (!tex) {
          tex = comp.createTextureFromCanvas(tc);
          this.textures.set(texKey, tex);
        } else {
          comp.gl.bindTexture(comp.gl.TEXTURE_2D, tex);
          comp.gl.texImage2D(
            comp.gl.TEXTURE_2D,
            0,
            comp.gl.RGBA,
            comp.gl.RGBA,
            comp.gl.UNSIGNED_BYTE,
            tc
          );
        }
        comp.drawLayer({
          texture: tex,
          transform,
          color,
          crossDissolve: dissolve,
          frameSize,
          effects: effectAmounts(clip.properties.effects),
        });
        continue;
      }

      if (!clip.assetId) continue;
      const asset = project.assets.find((a) => a.id === clip.assetId);
      if (!asset) continue;

      if (asset.kind === 'image') {
        let img = this.images.get(asset.id);
        if (!img) {
          const url = await resolveAssetUrl(asset);
          img = await loadImage(url);
          this.images.set(asset.id, img);
        }
        const texKey = `img-${asset.id}`;
        let tex = this.textures.get(texKey);
        if (!tex) {
          tex = comp.createTextureFromCanvas(img);
          this.textures.set(texKey, tex);
        }
        comp.drawLayer({
          texture: tex,
          transform,
          color,
          crossDissolve: dissolve,
          sourceSize: {
            width: img.naturalWidth || asset.width || width,
            height: img.naturalHeight || asset.height || height,
          },
          frameSize,
          effects: effectAmounts(clip.properties.effects),
        });
        continue;
      }

      if (asset.kind === 'audio') continue;

      let video = this.videos.get(asset.id);
      if (!video) {
        video = document.createElement('video');
        video.src = await resolveAssetUrl(asset);
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.crossOrigin = 'anonymous';
        this.videos.set(asset.id, video);
        await ensureVideoReady(video);
      }

      const sourceTime = clip.inPoint + local;
      await seekVideo(video, sourceTime);
      if (video.readyState < 2) continue;

      let tex = this.textures.get(asset.id);
      if (!tex) {
        tex = comp.createTextureFromVideo(video);
        this.textures.set(asset.id, tex);
      } else {
        comp.updateTextureFromVideo(tex, video);
      }

      const srcW = video.videoWidth || asset.width || width;
      const srcH = video.videoHeight || asset.height || height;

      comp.drawLayer({
        texture: tex,
        transform,
        color,
        crossDissolve: dissolve,
        sourceSize: { width: srcW, height: srcH },
        frameSize,
        effects: effectAmounts(clip.properties.effects),
      });
    }

    comp.endFrame();
    // Ensure pixels are ready for VideoFrame / MediaRecorder capture
    comp.gl.flush();
    comp.gl.finish();
  }

  destroy() {
    for (const v of this.videos.values()) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    this.videos.clear();
    this.images.clear();
    for (const tex of this.textures.values()) {
      this.compositor.gl.deleteTexture(tex);
    }
    this.textures.clear();
    this.compositor.destroy();
  }
}
